"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { api, humanMessage } from "../api";
import { setSessionUser } from "../session";
import type { AuthResult } from "../types";
import { fieldErrorsOf } from "./shared";

/*
 * 로그인 · 가입.
 *
 * 사용:
 *   const auth = useLogin();     // await auth.login(email, password, safeNext(searchParams.get("next")))
 *   const reg = useRegister();   // await reg.register({ email, password, name, mobile, sellerCode })
 *   (둘 다 { busy, error, fields } — fields 는 요청 필드 이름(snake_case) → 첫 오류 문구)
 *
 * 🔴 **성공하면 `router.replace`** — `push` 면 로그인 뒤 뒤로 가기가 로그인 화면입니다.
 *    성공 뒤에는 `busy` 를 풀지 않습니다 — 화면이 바뀔 때까지 버튼이 잠겨 있어야 두 번 안 눌립니다.
 * 🔴 **`keepOnUnauthorized`** — 여기서 401 은 «비밀번호가 틀렸다» 입니다. 세션 만료로 보고
 *    로그인 화면으로 다시 보내면 방금 쓴 오류 문구가 화면 전환에 지워집니다.
 */

/**
 * 로그인 뒤 돌아갈 자리. 🔴 **우리 앱 안의 경로만** 받습니다 — `next=https://…` 를 믿으면
 * 로그인 직후 바깥 사이트로 튕겨 보내는 통로가 됩니다(오픈 리다이렉트).
 */
export function safeNext(raw: string | null | undefined): string {
  const value = raw ?? "/";
  return value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

export function useLogin() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  async function login(email: string, password: string, next = "/"): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError("");
    setFields({});

    try {
      const result = await api.post<AuthResult>(
        "/auth/login",
        { email: email.trim().toLowerCase(), password },
        { keepOnUnauthorized: true },
      );
      setSessionUser(result.user);
      router.replace(safeNext(next));
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["email", "password"]));
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { login, busy, error, fields };
}

export type RegisterInput = {
  email: string;
  password: string;
  name: string;
  /** 선택 — 비우면 아예 보내지 않습니다 */
  mobile?: string;
  /** 🔴 가입 뒤에는 바꿀 수 없습니다 (`docs/base/10` §2-3) — 화면이 미리 알려야 합니다 */
  sellerCode?: string;
};

/**
 * 🔑 약관 동의는 **화면이 먼저 막습니다**(체크칸 옆에 오류). 이 훅은 두 동의가 끝난 뒤에만
 *    부르세요 — 요청에는 `agreements: {terms, privacy}` 를 참으로 싣습니다.
 */
export function useRegister() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  async function register(input: RegisterInput, next = "/"): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError("");
    setFields({});

    try {
      const result = await api.post<AuthResult>(
        "/auth/register",
        {
          email: input.email.trim().toLowerCase(),
          password: input.password,
          name: input.name.trim(),
          /* 🔑 빈 문자열이 아니라 아예 뺍니다 — «적지 않음» 과 «빈 값» 은 서버에서 다르게 검증될 수 있습니다. */
          mobile: input.mobile?.trim() || undefined,
          seller_code: input.sellerCode?.trim() || undefined,
          agreements: { terms: true, privacy: true },
        },
        { keepOnUnauthorized: true },
      );
      setSessionUser(result.user);
      router.replace(safeNext(next));
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["email", "password", "name", "mobile", "seller_code"]));
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { register, busy, error, fields };
}
